import React, { useMemo, useState } from "react";
import { arrayUnion, doc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../../../firebaseConfig";
import {
  addYearsToDate,
  cleanText,
  dateInputValue,
  getPacsIdentity,
  safeFilePart,
} from "./pacsWorkflowUtils";
import { buildLeaseAgreementHtml } from "./pacsOfficialTemplates";
import { downloadOfficialPdf, openOfficialPreview } from "./officialPdf";

const MAX_FILE_SIZE = 15 * 1024 * 1024;

const DOCUMENT_TYPES = [
  ["SIGNED_LEASE", "Executed & Registered Lease Agreement"],
  ["LAND_RECORD", "Land Document"],
];

const partyKey = (value) =>
  cleanText(value)
    .toUpperCase()
    .replace(/[^A-Z0-9\u0A80-\u0AFF\u0900-\u097F]/g, "");

const cleanFileName = (value) =>
  String(value || "document")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .slice(0, 120);

const makeForm = (profile, project) => {
  const identity = getPacsIdentity(profile, project);
  const saved = project.landLeaseDetails || {};
  const startDate = dateInputValue(
    saved.leaseStartDate || project.leaseAgreementDate,
  );
  const leaseYears = Number(saved.leaseYears || project.leaseYears || 20);
  const landOwnershipType =
    saved.landOwnershipType || identity.landOwnershipType || "OWNED";
  const savedLandOwnerName =
    saved.landOwnerName || project.landOwnerName || identity.landOwnerName || "";
  const landOwnerName =
    cleanText(landOwnershipType).toUpperCase() === "LEASED" &&
    partyKey(savedLandOwnerName) === partyKey(identity.societyName)
      ? ""
      : savedLandOwnerName;
  const previousAreaUnit = cleanText(
    saved.landAreaUnit || project.landAreaUnit || identity.landAreaUnit,
  ).toUpperCase();
  return {
    landOwnershipType,
    landOwnerName,
    landOwnerAddress: saved.landOwnerAddress || "",
    surveyNo: saved.surveyNo || identity.surveyNo || "",
    khataNo: saved.khataNo || identity.khataNo || "",
    landArea:
      saved.landAreaSqFt ||
      project.landAreaSqFt ||
      (previousAreaUnit === "SQ_FEET"
        ? saved.landArea || project.landArea || identity.landArea
        : ""),
    landAreaUnit: "SQ_FEET",
    leaseYears,
    leaseStartDate: startDate,
    leaseExpiryDate:
      dateInputValue(saved.leaseExpiryDate || project.leaseExpiryDate) ||
      addYearsToDate(startDate, leaseYears),
    annualRent: saved.annualRent || "",
    securityDeposit: saved.securityDeposit || "",
    executionPlace: saved.executionPlace || identity.village || "",
    executionDate: dateInputValue(saved.executionDate) || "",
    witness1Name: saved.witness1Name || "",
    witness2Name: saved.witness2Name || "",
  };
};

export default function PacsLandLeaseCenter({
  profile = {},
  project = {},
  onSaved,
}) {
  const identity = useMemo(() => getPacsIdentity(profile, project), [profile, project]);
  const [form, setForm] = useState(() => makeForm(profile, project));
  const [files, setFiles] = useState({});
  const [documents, setDocuments] = useState(project.landLeaseDocuments || {});
  const [agreementLanguage, setAgreementLanguage] = useState("GUJARATI");
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  const isLeased = cleanText(form.landOwnershipType).toUpperCase() === "LEASED";

  const mergedData = {
    ...identity,
    ...form,
    applicationId: identity.applicationId || project.id,
  };

  const set = (name, value) => {
    setForm((old) => {
      const next = { ...old, [name]: value };
      if (name === "leaseStartDate" || name === "leaseYears") {
        const start = name === "leaseStartDate" ? value : next.leaseStartDate;
        const years = name === "leaseYears" ? value : next.leaseYears;
        next.leaseExpiryDate = addYearsToDate(start, years);
      }
      if (
        name === "landOwnershipType" &&
        cleanText(value).toUpperCase() === "LEASED" &&
        partyKey(next.landOwnerName) === partyKey(identity.societyName)
      ) {
        next.landOwnerName = "";
      }
      return next;
    });
    if (message.text) setMessage({ type: "", text: "" });
  };

  const show = (text, type = "info") => setMessage({ text, type });

  const validate = () => {
    if (!project.id) return "Submitted PACS application is not available.";
    if (!form.landOwnershipType) return "Select land ownership/possession type.";
    if (!cleanText(form.surveyNo)) return "Enter survey/block number.";
    if (!cleanText(form.khataNo)) return "Enter khata/revenue record number.";
    if (!cleanText(form.landArea)) return "Enter land area.";
    if (isLeased) {
      if (!cleanText(form.landOwnerName)) return "Enter landowner/lessor name.";
      if (partyKey(form.landOwnerName) === partyKey(identity.societyName))
        return "First Party must be the actual landowner. First Party and Second Party PACS cannot be the same.";
      if (Number(form.leaseYears) < 20)
        return "Registered lease period must be at least 20 years.";
      if (!form.leaseStartDate || !form.leaseExpiryDate)
        return "Enter lease start and expiry dates.";
    }
    return "";
  };

  const saveDetails = async () => {
    const error = validate();
    if (error) return show(error, "error");
    const user = auth.currentUser;
    if (!user) return show("Login session has expired.", "error");
    try {
      setBusy("save");
      const now = new Date().toISOString();
      const payload = {
        ...form,
        landAreaUnit: "SQ_FEET",
        landAreaSqFt: form.landArea,
        leaseYears: isLeased ? Number(form.leaseYears) : 0,
        landLeaseStatus: isLeased ? "LEASE_DETAILS_COMPLETED" : "LAND_DETAILS_COMPLETED",
        updatedAtIso: now,
        updatedByUid: user.uid,
      };
      const projectUpdate = {
        landLeaseDetails: payload,
        landOwnership: form.landOwnershipType,
        landOwnershipType: form.landOwnershipType,
        ownershipType: form.landOwnershipType,
        landOwnerName: form.landOwnerName,
        surveyNo: form.surveyNo,
        landSurveyNo: form.surveyNo,
        khataNo: form.khataNo,
        khasraNo: form.khataNo,
        landArea: form.landArea,
        landAreaSqFt: form.landArea,
        landAreaUnit: "SQ_FEET",
        leaseYears: isLeased ? Number(form.leaseYears) : 0,
        leaseAgreementDate: isLeased ? form.leaseStartDate : "",
        leaseExpiryDate: isLeased ? form.leaseExpiryDate : "",
        landDocumentStage: payload.landLeaseStatus,
        updatedAt: serverTimestamp(),
        landLeaseHistory: arrayUnion({
          action: "LAND_LEASE_DETAILS_SAVED",
          ownershipType: form.landOwnershipType,
          savedAtIso: now,
          savedByUid: user.uid,
        }),
      };
      await updateDoc(doc(db, "godown_projects", project.id), projectUpdate);
      await setDoc(
        doc(db, "nafed_activity", `${project.id}_LAND_LEASE_${Date.now()}`),
        {
          action: "PACS_LAND_LEASE_DETAILS_SAVED",
          entityType: "GODOWN_PROJECT",
          entityId: project.id,
          projectId: project.id,
          applicationId: identity.applicationId,
          ownershipType: form.landOwnershipType,
          performedByUid: user.uid,
          performedByRole: "PACS",
          createdAt: serverTimestamp(),
          createdAtIso: now,
        },
      );
      onSaved?.(projectUpdate);
      show("Land/lease details saved successfully.", "success");
    } catch (saveError) {
      console.error(saveError);
      show(saveError.message || "Land/lease details could not be saved.", "error");
    } finally {
      setBusy("");
    }
  };

  const selectFile = (type, file) => {
    if (!file) return setFiles((old) => ({ ...old, [type]: null }));
    if (file.size > MAX_FILE_SIZE)
      return show("Each land/lease document must be 15 MB or smaller.", "error");
    setFiles((old) => ({ ...old, [type]: file }));
  };

  const uploadSelected = async () => {
    const user = auth.currentUser;
    if (!user || !project.id) return show("Application/login is unavailable.", "error");
    const selected = Object.entries(files).filter(([, file]) => file);
    if (!selected.length) return show("Select at least one document to upload.", "error");
    try {
      setBusy("upload");
      const next = { ...documents };
      for (const [type, file] of selected) {
        const version = Number(next[type]?.version || 0) + 1;
        const storagePath =
          `nafed_pacs/${user.uid}/${project.id}/land_lease/${type}/` +
          `v${version}_${Date.now()}_${cleanFileName(file.name)}`;
        const objectRef = ref(storage, storagePath);
        await uploadBytes(objectRef, file, {
          contentType: file.type || "application/octet-stream",
          customMetadata: {
            projectId: project.id,
            applicationId: identity.applicationId,
            documentType: type,
            uploadedByUid: user.uid,
          },
        });
        next[type] = {
          type,
          title: DOCUMENT_TYPES.find(([code]) => code === type)?.[1] || type,
          fileName: file.name,
          storagePath,
          url: await getDownloadURL(objectRef),
          version,
          status: "UPLOADED",
          uploadedAtIso: new Date().toISOString(),
          uploadedByUid: user.uid,
        };
      }
      const stage = isLeased
        ? next.SIGNED_LEASE?.url && next.LAND_RECORD?.url
          ? "LEASE_DOCUMENTS_COMPLETE"
          : next.SIGNED_LEASE?.url
            ? "REGISTERED_LEASE_UPLOADED_LAND_DOCUMENT_PENDING"
            : next.LAND_RECORD?.url
              ? "LAND_DOCUMENT_UPLOADED_REGISTERED_LEASE_PENDING"
              : "LEASE_DOCUMENTS_PENDING"
        : next.LAND_RECORD?.url
          ? "OWN_LAND_DOCUMENT_UPLOADED"
          : "LAND_DOCUMENTS_PENDING";
      const update = {
        landLeaseDocuments: next,
        landDocumentStage: stage,
        signedLeaseAgreementUrl: next.SIGNED_LEASE?.url || "",
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "godown_projects", project.id), update);
      setDocuments(next);
      setFiles({});
      onSaved?.(update);
      show("Selected land/lease documents uploaded successfully.", "success");
    } catch (uploadError) {
      console.error(uploadError);
      show(uploadError.message || "Documents could not be uploaded.", "error");
    } finally {
      setBusy("");
    }
  };

  const createAgreement = async (action) => {
    const error = validate();
    if (error) return show(error, "error");
    if (!isLeased) return show("Lease Agreement is available only for Lease Land.", "error");
    try {
      const html = buildLeaseAgreementHtml(mergedData, agreementLanguage);
      if (action === "PREVIEW") {
        openOfficialPreview(html);
        return;
      }
      setBusy("leasePdf");
      const suffix = agreementLanguage === "GUJARATI" ? "Gujarati" : "English";
      await downloadOfficialPdf(
        html,
        `Registered_Lease_Deed_${safeFilePart(identity.societyName)}_${suffix}.pdf`,
      );
      show(`${suffix} registered lease PDF downloaded successfully.`, "success");
    } catch (error) {
      show(error.message || "Lease Agreement could not be generated.", "error");
    } finally {
      setBusy((current) => (current === "leasePdf" ? "" : current));
    }
  };

  return (
    <div className="land-center">
      <style>{STYLE}</style>
      <header className="land-hero">
        <div><small>PACS LAND DOCUMENT WORKSPACE</small><h2>Land Ownership & 20-Year Registered Lease</h2><p>Enter only Survey Number, Khata Number and area in square feet. Select Gujarati or English, download the PDF or preview it, then execute and register the deed.</p></div>
        <b>{project.landDocumentStage || "DETAILS PENDING"}</b>
      </header>
      {message.text && <div className={`land-message ${message.type}`}>{message.text}<button onClick={() => setMessage({ type: "", text: "" })}>×</button></div>}

      <section className="land-card">
        <h3>1. Basic Land Details</h3>
        <div className="land-grid">
          <Select label="Land Ownership / Possession" value={form.landOwnershipType} onChange={(value) => set("landOwnershipType", value)} options={[["OWNED", "Owned by PACS"], ["LEASED", "20-Year Lease Land"], ["ALLOTTED", "Government / Institutional Allotment"]]} />
          <Field label="Survey / Block Number" value={form.surveyNo} onChange={(value) => set("surveyNo", value)} />
          <Field label="Khata / Revenue Record Number" value={form.khataNo} onChange={(value) => set("khataNo", value)} />
          <Field label="Land Area (Square Feet)" type="number" min="1" value={form.landArea} onChange={(value) => set("landArea", value)} />
        </div>
      </section>

      {isLeased && <>
        <section className="land-card">
          <h3>2. First Party, Second Party and Lease Details</h3>
          <div className="party-summary"><div><small>FIRST PARTY / LANDOWNER / LESSOR</small><b>{form.landOwnerName || "Enter actual landowner name below"}</b><span>Person/entity whose land is being leased</span></div><div><small>SECOND PARTY / PACS / LESSEE</small><b>{identity.societyName}</b><span>{identity.registrationNumber}</span></div></div>
          <div className="land-grid">
            <Field label="First Party – Actual Landowner Full Name" value={form.landOwnerName} onChange={(value) => set("landOwnerName", value)} />
            <Field label="First Party – Landowner Address" value={form.landOwnerAddress} onChange={(value) => set("landOwnerAddress", value)} />
            <Field label="Lease Period (Years)" type="number" min="20" value={form.leaseYears} onChange={(value) => set("leaseYears", value)} />
            <Field label="Lease Start Date" type="date" value={form.leaseStartDate} onChange={(value) => set("leaseStartDate", value)} />
            <Field label="Lease Expiry Date" type="date" value={form.leaseExpiryDate} onChange={(value) => set("leaseExpiryDate", value)} />
            <Field label="Annual Rent / Consideration (₹)" type="number" value={form.annualRent} onChange={(value) => set("annualRent", value)} />
            <Field label="Security Deposit (₹)" type="number" value={form.securityDeposit} onChange={(value) => set("securityDeposit", value)} />
            <Field label="Witness 1 Name" value={form.witness1Name} onChange={(value) => set("witness1Name", value)} />
            <Field label="Witness 2 Name" value={form.witness2Name} onChange={(value) => set("witness2Name", value)} />
            <Field label="Execution Place" value={form.executionPlace} onChange={(value) => set("executionPlace", value)} />
            <Field label="Execution Date" type="date" value={form.executionDate} onChange={(value) => set("executionDate", value)} />
          </div>
          <div className="lease-process-note"><b>Process:</b> Select language → Download PDF or Preview → legal/title verification → print on proper stamp paper → obtain both-party and witness signatures → present it before the competent Sub-Registrar → upload the executed registered deed below.</div>
          <div className="agreement-generator">
            <Select label="Agreement Language" value={agreementLanguage} onChange={setAgreementLanguage} options={[["GUJARATI", "ગુજરાતી (Gujarati)"], ["ENGLISH", "English"]]} />
            <div className="agreement-actions"><button className="primary" disabled={Boolean(busy)} onClick={() => createAgreement("PDF")}>{busy === "leasePdf" ? "Creating PDF..." : "Download PDF"}</button><button disabled={Boolean(busy)} onClick={() => createAgreement("PREVIEW")}>Preview</button></div>
          </div>
        </section>
      </>}

      <section className="land-card">
        <h3>{isLeased ? "3" : "2"}. Final Document Upload</h3>
        <p className="upload-help">{isLeased ? "Only two documents are required: executed registered lease agreement and land document." : "Only the land document is required for PACS-owned land."}</p>
        <div className="upload-list">
          {DOCUMENT_TYPES.filter(([type]) => isLeased || type === "LAND_RECORD").map(([type, title]) => (
            <label key={type}><div><b>{title}</b><span>{documents[type]?.fileName || "Not uploaded"}</span></div><input type="file" accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png" onChange={(event) => selectFile(type, event.target.files?.[0] || null)} />{documents[type]?.url && <a href={documents[type].url} target="_blank" rel="noreferrer">View</a>}</label>
          ))}
        </div>
        <div className="land-footer"><button onClick={saveDetails} disabled={Boolean(busy)}>{busy === "save" ? "Saving..." : "Save Land / Lease Details"}</button><button className="primary" onClick={uploadSelected} disabled={Boolean(busy)}>{busy === "upload" ? "Uploading..." : "Upload Selected Documents"}</button></div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", min, wide }) {
  return <label className={wide ? "land-field wide" : "land-field"}><span>{label}</span><input type={type} min={min} value={value ?? ""} onChange={(event) => onChange(event.target.value)} /></label>;
}

function Select({ label, value, onChange, options }) {
  return <label className="land-field"><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}>{options.map(([id, title]) => <option value={id} key={id}>{title}</option>)}</select></label>;
}

const STYLE = `
  .land-center{display:grid;gap:18px;color:#17233a}.land-hero{display:flex;justify-content:space-between;align-items:center;background:linear-gradient(135deg,#062b58,#0b579a);color:white;border-radius:18px;padding:25px 28px;box-shadow:0 16px 35px #0b35642b}.land-hero small{font-weight:800;letter-spacing:.11em;color:#b9dcff}.land-hero h2{font-size:29px;margin:6px 0}.land-hero p{margin:0;max-width:850px;color:#dfedfb}.land-hero>b{background:#ffffff1a;border:1px solid #ffffff40;border-radius:999px;padding:10px 14px;font-size:12px}.land-message{display:flex;justify-content:space-between;padding:13px 16px;border-radius:12px;font-weight:700}.land-message.error{background:#fff0ef;color:#a52e25}.land-message.success{background:#eaf9f0;color:#167342}.land-message.info{background:#eef6ff;color:#185c96}.land-message button{border:0;background:none;font-size:20px}.land-card{background:white;border:1px solid #dbe4ee;border-radius:16px;padding:22px;box-shadow:0 8px 24px #173b650c}.land-card h3{margin:0 0 17px;color:#092f5a;font-size:19px}.land-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}.land-field{display:grid;gap:6px}.land-field.wide{grid-column:1/-1}.land-field span{font-size:12px;font-weight:800;color:#4e6077}.land-field input,.land-field select{width:100%;border:1px solid #c7d3df;border-radius:10px;padding:11px 12px;background:#fbfdff;font:inherit;color:#17233a}.party-summary{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px}.party-summary>div{border:1px solid #d3dfeb;background:#f7faff;border-radius:12px;padding:13px;display:grid;gap:4px}.party-summary small{font-size:10px;color:#607590;font-weight:900}.party-summary span{font-size:11px;color:#68798d}.lease-process-note{margin-top:16px;border:1px solid #c9dced;background:#f1f7fd;color:#345777;border-radius:10px;padding:13px;line-height:1.55}.lease-process-note b{color:#0b4f88}.agreement-generator{display:grid;grid-template-columns:minmax(250px,360px) 1fr;align-items:end;gap:16px;margin-top:17px;padding:15px;border:1px solid #d4e0ec;background:#f8fbff;border-radius:12px}.agreement-actions,.land-footer{display:flex;flex-wrap:wrap;gap:9px;margin-top:18px}.agreement-generator .agreement-actions{justify-content:flex-end;margin-top:0}.agreement-actions button,.land-footer button{border:1px solid #0d5a9d;background:white;color:#0d4d85;border-radius:10px;padding:10px 18px;font-weight:800;cursor:pointer}.agreement-actions button.primary,.land-footer button.primary{background:#0875bf;color:white}.agreement-actions button:disabled,.land-footer button:disabled{opacity:.55;cursor:not-allowed}.upload-help{margin:-6px 0 14px;color:#607389;font-size:13px}.upload-list{display:grid;gap:10px}.upload-list label{display:grid;grid-template-columns:1fr minmax(210px,310px) auto;align-items:center;gap:12px;border:1px solid #dae4ee;border-radius:12px;padding:12px}.upload-list label div{display:grid;gap:3px}.upload-list label span{font-size:11px;color:#68798d}.upload-list a{color:#0875bf;font-weight:800}.land-footer{justify-content:flex-end;border-top:1px solid #e4eaf1;padding-top:16px}@media(max-width:820px){.land-hero{align-items:flex-start;gap:15px;flex-direction:column}.land-grid,.party-summary,.agreement-generator{grid-template-columns:1fr}.land-field.wide{grid-column:auto}.upload-list label{grid-template-columns:1fr}.land-footer button,.agreement-actions button{width:100%}}
`;
